import React, { useEffect, useRef } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { environment } from "../../config/environment";

const getLogLevelColor = (message) => {
  if (/\[ERROR\]|\bERROR\b|Traceback|Task timed out/i.test(message)) {
    return "text-red-400";
  }
  if (/\[WARN(?:ING)?\]|\bWARN(?:ING)?\b/i.test(message)) return "text-amber-300";
  if (/^(START|END|REPORT) RequestId:/.test(message)) return "text-gray-500";
  if (/\[INFO\]|\bINFO\b/i.test(message)) return "text-sky-300";
  return "text-gray-200";
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  return new Date(timestamp).toLocaleTimeString("en-US", { hour12: false });
};

const buildCloudWatchLogUrl = (functionName, requestId) => {
  if (!functionName) return null;
  const region = environment.awsRegion || "us-east-1";
  const logGroup = encodeURIComponent(encodeURIComponent(`/aws/lambda/${functionName}`));
  const base = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${logGroup}`;
  if (!requestId) return base;
  const filter = encodeURIComponent(`"${requestId}"`);
  return `${base}/log-events$3FfilterPattern$3D${filter}`;
};

const LambdaLogPanel = ({
  lines = [],
  status,
  finished,
  unavailable,
  error,
  onRefresh,
  refreshing = false,
  requestId,
  functionName,
  heightClass = "max-h-72",
}) => {
  const scrollRef = useRef(null);

  // Follow the tail while the invocation is still producing output.
  useEffect(() => {
    if (finished || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines, finished]);

  const isRunning = status === "running";

  return (
    <div className="rounded-md border border-gray-300 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-100 border-b border-gray-300">
        <div className="min-w-0">
          {(() => {
            const cwUrl = buildCloudWatchLogUrl(functionName, requestId);
            return cwUrl ? (
              <a
                href={cwUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
              >
                Execution logs
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            ) : (
              <p className="text-xs font-medium text-gray-700">Execution logs</p>
            );
          })()}
          {(functionName || requestId) && (
            <p
              className="text-[10px] text-gray-500 truncate"
              title={`${functionName || ""} ${requestId || ""}`.trim()}
            >
              {functionName}
              {functionName && requestId ? " · " : ""}
              {requestId}
            </p>
          )}
        </div>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden />
            {refreshing ? "Fetching…" : "Refresh"}
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className={`bg-gray-900 p-3 overflow-auto font-mono text-xs ${heightClass}`}
      >
        {unavailable && (
          <p className="text-amber-300">
            Logs are unavailable for this function. The log group may not
            exist, retention may have expired, or this environment may not have
            permission to read it. The invocation itself was still sent.
          </p>
        )}

        {!unavailable && lines.length === 0 && (
          <p className="text-gray-400">
            {isRunning
              ? "Waiting for logs… CloudWatch usually lags a few seconds behind execution."
              : "No log output for this invocation."}
          </p>
        )}

        {lines.map((line, index) => {
          const time = formatTimestamp(line.timestamp);
          return (
            <div key={`${line.timestamp ?? "n"}-${index}`} className="flex gap-2">
              {time && (
                <span className="shrink-0 text-gray-600 select-none">{time}</span>
              )}
              <pre
                className={`whitespace-pre-wrap break-words ${getLogLevelColor(
                  line.message
                )}`}
              >
                {line.message}
              </pre>
            </div>
          );
        })}

        {!unavailable && isRunning && lines.length > 0 && (
          <p className="mt-2 text-gray-500">Still running…</p>
        )}
      </div>

      {error && (
        <p className="px-3 py-1.5 text-[11px] text-red-700 bg-red-50 border-t border-red-200">
          {error}
        </p>
      )}
    </div>
  );
};

export default LambdaLogPanel;
